/**
 * CELL-EXIT KEYS — the one rule an editor's change handler must not break.
 *
 * An editor that reports a change usually wants the grid to stay out of it
 * (a click on an option must not re-enter or leave the cell). But two keys
 * exist ONLY to leave the cell, and the grid — not the editor — owns what
 * happens next:
 *
 *     Tab    ──▶ commit, focus the next cell    ┐ the grid's job,
 *     Enter  ──▶ commit, focus the row below    ┘ never the editor's.
 *
 * Swallowing one costs the user a whole keypress. MUI's Autocomplete consumes
 * Enter #1 to pick the highlighted option, and that fires `onChange` with the
 * SAME keydown event; a blanket `stopPropagation()` there kills the event
 * before `GridCell` can publish `cellKeyDown`, so `useGridRowEditing` never
 * sees the Enter and the row stays open:
 *
 *   Enter #1  input ─▶ useAutocomplete ─▶ selectNewValue ─▶ onChange
 *                                                            │
 *                            stopPropagation() ──╳── GridCell.cellKeyDown
 *                                                            ╳
 *                                             rowEditStop ── never fires
 *   Enter #2  listbox now closed, nothing consumes it ─▶ commit + advance
 *
 * That is QA-4/7: byte-identical state between two physical presses. Value,
 * mode and focus are all already correct on press #1 — the event only has to
 * be allowed to finish its trip.
 *
 * ORDERING is safe by construction: `setEditCellValue` writes the edit state
 * SYNCHRONOUSLY unless the column defines `preProcessEditCellProps` (the only
 * deferring branch — no column in this app defines one), and it runs before
 * the event resumes bubbling. The grid therefore diffs a row that already
 * holds the selection; this is NOT the stale-row-at-diff window that the
 * save-pipeline fix closes.
 *
 * ⚠ THIS MODULE IMPORTS NOTHING, and it lives outside `modules/` on purpose.
 * Its callers are leaf editor cells (`AutoCompleteCell` and friends) that sit
 * in `components/`, `hooks/` and `modules/expenses/`. Re-exporting it from any
 * grid barrel drags `useGridTabNavigation` -> `gridRows.store` in with them;
 * that was fatal at collect time once ("createUseStoreState is not a
 * function"). Before ever barrelling it, run
 * `LotSelectCell.filterGuard.test.tsx` — the spec that caught that failure.
 */

/**
 * Shift+Enter is deliberately included: the document keymap claims it in the
 * capture phase (`useGridTabNavigation.handleShiftEnter`) and it never reaches
 * an editor — but if it ever does, commit-and-move-up is still the grid's call.
 */
const CELL_EXIT_KEYS: ReadonlySet<string> = new Set(['Tab', 'Enter']);

/**
 * Did this change come from a key whose whole job is to leave the cell?
 * `onChange` is also fired by clicks and blurs, which carry no `key` at all.
 */
export const isCellExitKey = (event: { key?: string }): boolean => CELL_EXIT_KEYS.has(event.key ?? '');

/**
 * Keep an editor's change event local — UNLESS the user is leaving the cell,
 * in which case the grid must see the keydown to commit and move focus.
 */
export const stopPropagationUnlessCellExit = (event: { key?: string; stopPropagation: () => void }): void => {
  if (isCellExitKey(event)) {
    return;
  }

  event.stopPropagation();
};
